import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import type { ProjectDataTableItem } from '@kirin/api-client/contracts/projects';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface CellProps {
  row: ProjectDataTableItem;
  organizationId: string;
}

/** Project name → link to the single-project page. */
export function ProjectNameCell({
  row,
  organizationId,
}: CellProps): ReactElement {
  return (
    <Link
      to={`/${organizationId}/inventory/projects/${encodeURIComponent(row.slug)}`}
      className="font-medium text-primary hover:underline"
    >
      {row.name}
    </Link>
  );
}

/** Git origin URL, or an em-dash for repo-less folder projects. */
export function RepoUrlCell({
  row,
}: {
  row: ProjectDataTableItem;
}): ReactElement {
  return (
    <span className="text-muted-foreground">{row.repoOriginUrl ?? '—'}</span>
  );
}

/**
 * User count → opens a modal listing the project's users (mirrors the MCP /
 * Instructions tabs). Plain text when there are no users or no click handler.
 */
export function UsersCountCell({
  row,
  onSelectProject,
}: {
  row: ProjectDataTableItem;
  onSelectProject?: (project: ProjectDataTableItem) => void;
}): ReactElement {
  const count = (
    <span className="inline-flex items-center gap-1.5 font-medium text-muted-foreground">
      <Users className="h-3.5 w-3.5" />
      {row.userCount}
    </span>
  );

  if (!onSelectProject || row.userCount === 0) {
    return count;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => onSelectProject(row)}
          className="inline-flex items-center gap-1.5 font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors cursor-pointer"
        >
          <Users className="h-3.5 w-3.5" />
          {row.userCount}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="h-3 w-3" />
            <span>
              {row.userCount} user{row.userCount !== 1 ? 's' : ''} on this
              project
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Click to see user list
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * AI instruction count → link to the AI Instructions tab pre-filtered to this
 * project (the AI-instructions side reads the `project` query param).
 */
export function AiInstructionsCell({
  row,
  organizationId,
}: CellProps): ReactElement {
  const label = `${row.aiInstructionsCount} instruction file(s)`;
  if (row.aiInstructionsCount === 0) {
    return <span className="text-muted-foreground">{label}</span>;
  }
  return (
    <Link
      to={`/${organizationId}/inventory?tab=ai-instructions&org-ai-ins.ip[includes]=${encodeURIComponent(row.slug)}`}
      className="text-primary hover:underline"
    >
      {label}
    </Link>
  );
}

/**
 * Hook count → link to the Hooks tab pre-filtered to this project (the Hooks
 * Projects column filter is entity-based, keyed by project slug). Zero stays
 * plain text — nothing to link to.
 */
export function HooksCell({ row, organizationId }: CellProps): ReactElement {
  const label = `${row.hooksCount} hook(s)`;
  if (row.hooksCount === 0) {
    return <span className="text-muted-foreground">{label}</span>;
  }
  return (
    <Link
      to={`/${organizationId}/inventory?tab=hooks&org-hooks.hpr[includes]=${encodeURIComponent(row.slug)}`}
      className="text-primary hover:underline"
    >
      {label}
    </Link>
  );
}
